import { useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2, User as UserIcon, Calendar, Wallet, Sparkles, Mail, Phone, Globe, IdCard, Bell, MessageSquare, TrendingUp, PenTool, MapPin, Monitor, Camera as CameraIcon, FileText, Download } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar } from "@/components/Avatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal showing full user dossier — profile, appointments, payments, readings + stats.
 * Opens when admin clicks user name in chat dock or row.
 */
export const UserDossierModal = ({ userId, onClose }) => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("info");

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    axios.get(`${API}/admin/users/${userId}/dossier`, { headers: authHeader() })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId, authHeader]);

  if (!userId) return null;
  const u = data?.user || {};
  const s = data?.stats || {};

  return (
    <div className="fixed inset-0 z-[180] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="user-dossier-modal" onClick={onClose}>
      <div className="bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] border border-[#d4af37]/30 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-[#d4af37]/10 to-amber-500/10">
          <div className="flex items-center gap-3">
            <Avatar userId={userId} name={u.name || u.username} hasPicHint={u.has_profile_pic} authHeader={authHeader} size={48} />
            <div>
              <div className="font-serif text-xl text-white">{u.name || u.username}</div>
              <div className="text-[11px] text-neutral-400 font-mono flex items-center gap-2">
                <IdCard className="w-3 h-3" /> {u.visible_id}
                {u.blocked && <span className="text-red-300 ml-1">· Bloqueado</span>}
                {u.online && <span className="text-emerald-300 ml-1">● En línea</span>}
              </div>
            </div>
          </div>
          <button data-testid="dossier-close" onClick={onClose} className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>

        {/* Actions bar — download full expediente as PDF */}
        {!loading && data && (
          <div className="px-5 py-2.5 border-b border-white/5 bg-black/30 flex justify-end">
            <button
              data-testid="dossier-download-pdf"
              onClick={async () => {
                try {
                  const r = await fetch(`${API}/admin/users/${userId}/dossier.pdf`, { headers: authHeader() });
                  if (!r.ok) return;
                  const blob = await r.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `expediente_${(u.visible_id || userId.slice(0,8))}.pdf`;
                  document.body.appendChild(a); a.click();
                  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
                } catch (e) { /* silent */ }
              }}
              className="text-[11px] px-3 py-1.5 rounded-full border border-[#d4af37]/50 bg-[#d4af37]/10 text-[#d4af37] hover:bg-[#d4af37]/20 flex items-center gap-1.5 font-semibold uppercase tracking-widest"
              title="Descargar expediente completo en PDF"
            >
              <Download className="w-3.5 h-3.5" /> Expediente PDF
            </button>
          </div>
        )}

        {loading || !data ? (
          <div className="flex-1 flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-[#d4af37] animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats strip */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 px-5 py-3 bg-black/40 border-b border-white/5">
              <Stat icon={Wallet} color="emerald" label="Pagado" value={`$${(s.total_paid || 0).toLocaleString()}`} testid="stat-paid" />
              <Stat icon={TrendingUp} color="amber" label="Pendiente" value={`$${(s.pending || 0).toLocaleString()}`} testid="stat-pending" />
              <Stat icon={Calendar} color="sky" label="Citas" value={s.appointments_count} testid="stat-appts" />
              <Stat icon={Sparkles} color="purple" label="Lecturas" value={s.readings_count} testid="stat-readings" />
              <Stat icon={PenTool} color="rose" label="Firmas" value={s.signatures_signed || 0} testid="stat-signatures" />
            </div>
            {/* Tabs */}
            <div className="flex gap-1 border-b border-white/5 px-3 bg-black/30 text-xs overflow-x-auto">
              {[
                { id: "info", label: "Información" },
                { id: "appts", label: `Citas (${data.appointments.length})` },
                { id: "pays", label: `Pagos (${data.plans.length})` },
                { id: "readings", label: `Lecturas (${data.readings.length})` },
                { id: "signatures", label: `Contratos (${(data.signatures || []).length})` },
                { id: "petitions", label: `Peticiones (${(data.petitions || []).length})` },
                { id: "bookings", label: `Reservas (${(data.bookings || []).length})` },
                { id: "devices", label: `Dispositivos (${(data.devices || []).length})` },
              ].map((t) => (
                <button
                  key={t.id}
                  data-testid={`dossier-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`px-3 py-2 border-b-2 ${tab === t.id ? "border-[#d4af37] text-[#d4af37]" : "border-transparent text-neutral-300 hover:text-white"}`}
                >{t.label}</button>
              ))}
            </div>
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-5">
              {tab === "info" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <Row icon={Mail} label="Email" value={u.email} />
                  <Row icon={Phone} label="Teléfono" value={u.phone || "—"} />
                  <Row icon={Globe} label="País" value={u.country || "—"} />
                  <Row icon={UserIcon} label="Edad" value={u.age ? `${u.age} años` : "—"} />
                  <Row icon={IdCard} label="Creado" value={u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"} />
                  <Row icon={Bell} label="Notificaciones" value={s.notifications_count} />
                  <Row icon={MessageSquare} label="Mensajes totales" value={s.messages_count} />
                  <Row icon={Sparkles} label="Horóscopo" value={u.horoscope_access_until ? `hasta ${new Date(u.horoscope_access_until).toLocaleDateString()}` : "sin acceso"} />
                  {/* Última ubicación conocida (captura silenciosa o link) */}
                  {u.last_geo && (
                    <div className="md:col-span-2 bg-black/30 border border-white/5 rounded-lg p-3 flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-[#d4af37] flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Última ubicación conocida</div>
                        <div className="text-sm text-white">
                          {u.last_geo.lat?.toFixed(6)}, {u.last_geo.lng?.toFixed(6)}
                          {u.last_geo.accuracy ? <span className="text-neutral-500 ml-1">(±{Math.round(u.last_geo.accuracy)}m)</span> : null}
                        </div>
                        <div className="text-[10px] text-neutral-500 mt-0.5">
                          {u.last_geo.captured_at ? new Date(u.last_geo.captured_at).toLocaleString() : ""}
                          {u.last_geo.source ? ` · vía ${u.last_geo.source}` : ""}
                        </div>
                      </div>
                      <a
                        href={`https://www.google.com/maps?q=${u.last_geo.lat},${u.last_geo.lng}`}
                        target="_blank" rel="noreferrer"
                        className="btn-outline-gold text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full"
                      >Ver mapa</a>
                    </div>
                  )}
                  {/* Datos de identificación oficial (si el cliente ya firmó) */}
                  {u.ine_data && (
                    <div className="md:col-span-2 bg-purple-500/5 border border-purple-500/25 rounded-lg p-3">
                      <div className="text-[10px] uppercase tracking-widest text-purple-300 mb-2 flex items-center gap-1">
                        <IdCard className="w-3 h-3" /> Datos de identificación oficial capturados
                        {u.ine_data.tipo_documento && (
                          <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-purple-500/20 border border-purple-500/40 normal-case tracking-normal">
                            {u.ine_data.tipo_documento}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-neutral-200">
                        {u.ine_data.nombre_completo && <div><span className="text-neutral-500">Nombre:</span> <span className="text-white">{u.ine_data.nombre_completo}</span></div>}
                        {u.ine_data.nombres && !u.ine_data.nombre_completo && <div><span className="text-neutral-500">Nombres:</span> <span className="text-white">{u.ine_data.nombres}</span></div>}
                        {u.ine_data.apellido_paterno && <div><span className="text-neutral-500">Ap. paterno:</span> <span className="text-white">{u.ine_data.apellido_paterno}</span></div>}
                        {u.ine_data.apellido_materno && <div><span className="text-neutral-500">Ap. materno:</span> <span className="text-white">{u.ine_data.apellido_materno}</span></div>}
                        {u.ine_data.curp && <div><span className="text-neutral-500">CURP:</span> <span className="font-mono text-white">{u.ine_data.curp}</span></div>}
                        {u.ine_data.clave_de_elector && <div><span className="text-neutral-500">Clave elector:</span> <span className="font-mono text-white">{u.ine_data.clave_de_elector}</span></div>}
                        {u.ine_data.licencia_numero && <div><span className="text-neutral-500">Licencia N°:</span> <span className="font-mono text-white">{u.ine_data.licencia_numero}</span></div>}
                        {u.ine_data.licencia_categoria && <div><span className="text-neutral-500">Categoría:</span> <span className="text-white">{u.ine_data.licencia_categoria}</span></div>}
                        {u.ine_data.pasaporte_numero && <div><span className="text-neutral-500">Pasaporte N°:</span> <span className="font-mono text-white">{u.ine_data.pasaporte_numero}</span></div>}
                        {u.ine_data.documento_numero && <div><span className="text-neutral-500">Documento N°:</span> <span className="font-mono text-white">{u.ine_data.documento_numero}</span></div>}
                        {u.ine_data.fecha_nacimiento && <div><span className="text-neutral-500">F. Nacimiento:</span> <span className="text-white">{u.ine_data.fecha_nacimiento}</span></div>}
                        {u.ine_data.lugar_nacimiento && <div><span className="text-neutral-500">Lugar nac.:</span> <span className="text-white">{u.ine_data.lugar_nacimiento}</span></div>}
                        {u.ine_data.nacionalidad && <div><span className="text-neutral-500">Nacionalidad:</span> <span className="text-white">{u.ine_data.nacionalidad}</span></div>}
                        {u.ine_data.sexo && <div><span className="text-neutral-500">Sexo:</span> <span className="text-white">{u.ine_data.sexo}</span></div>}
                        {u.ine_data.domicilio && <div className="sm:col-span-2"><span className="text-neutral-500">Domicilio:</span> <span className="text-white">{u.ine_data.domicilio}</span></div>}
                        {u.ine_data.entidad && <div><span className="text-neutral-500">Entidad:</span> <span className="text-white">{u.ine_data.entidad}</span></div>}
                        {u.ine_data.municipio && <div><span className="text-neutral-500">Municipio:</span> <span className="text-white">{u.ine_data.municipio}</span></div>}
                        {u.ine_data.pais_emisor && <div><span className="text-neutral-500">País emisor:</span> <span className="text-white">{u.ine_data.pais_emisor}</span></div>}
                        {u.ine_data.entidad_emisora && <div><span className="text-neutral-500">Emisor:</span> <span className="text-white">{u.ine_data.entidad_emisora}</span></div>}
                        {u.ine_data.fecha_expedicion && <div><span className="text-neutral-500">F. Expedición:</span> <span className="text-white">{u.ine_data.fecha_expedicion}</span></div>}
                        {u.ine_data.fecha_vencimiento && <div><span className="text-neutral-500">F. Vencimiento:</span> <span className="text-white">{u.ine_data.fecha_vencimiento}</span></div>}
                        {u.ine_data.vigencia && <div><span className="text-neutral-500">Vigencia:</span> <span className="text-white">{u.ine_data.vigencia}</span></div>}
                        {u.ine_data.folio && <div><span className="text-neutral-500">Folio:</span> <span className="font-mono text-white">{u.ine_data.folio}</span></div>}
                        {u.ine_data.mrz && <div className="sm:col-span-2"><span className="text-neutral-500">MRZ:</span> <span className="font-mono text-white text-[10px] break-all">{u.ine_data.mrz}</span></div>}
                      </div>
                      <div className="text-[10px] text-purple-300/70 mt-2">
                        Capturado el {u.ine_data.captured_at ? new Date(u.ine_data.captured_at).toLocaleString() : "—"}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {tab === "signatures" && (
                <div className="space-y-2">
                  {(data.signatures || []).length === 0 && <p className="text-neutral-500 italic text-sm">Sin contratos firmados.</p>}
                  {(data.signatures || []).map((sg) => (
                    <div key={sg.id} data-testid={`dossier-sig-${sg.id}`} className="bg-black/40 border border-white/10 rounded-lg p-3 text-sm">
                      <div className="flex justify-between items-start mb-1">
                        <div>
                          <div className="text-white font-semibold flex items-center gap-1.5">
                            <PenTool className="w-3.5 h-3.5 text-purple-300" /> {sg.service_name}
                          </div>
                          <div className="text-[10px] text-neutral-500">
                            Firmado {sg.signed_at ? new Date(sg.signed_at).toLocaleString() : "—"}
                          </div>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                          sg.status === "completed" ? "border-emerald-500/40 text-emerald-300"
                          : sg.status === "signed_pending_id" ? "border-amber-500/40 text-amber-300"
                          : "border-neutral-500/40 text-neutral-300"
                        }`}>{sg.status}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[11px] mt-2">
                        <div><span className="text-neutral-500">Monto:</span> <span className="text-white">${sg.amount_paid?.toLocaleString()} {sg.currency}</span></div>
                        {sg.capture?.ip && <div><span className="text-neutral-500">IP:</span> <span className="font-mono text-white">{sg.capture.ip}</span></div>}
                        {sg.capture?.geo?.lat && <div className="col-span-2"><span className="text-neutral-500">GPS:</span> <a href={`https://www.google.com/maps?q=${sg.capture.geo.lat},${sg.capture.geo.lng}`} target="_blank" rel="noreferrer" className="text-sky-300 hover:text-sky-200 underline">{sg.capture.geo.lat.toFixed(4)}, {sg.capture.geo.lng.toFixed(4)}</a></div>}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-white/5">
                        <button
                          data-testid={`dossier-sig-pdf-${sg.id}`}
                          onClick={async () => {
                            const r = await fetch(`${API}/admin/signatures/${sg.id}/pdf`, { headers: authHeader() });
                            if (!r.ok) return;
                            const blob = await r.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url; a.download = `contrato_${sg.id.slice(0,8)}.pdf`;
                            document.body.appendChild(a); a.click();
                            setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
                          }}
                          className="text-[10px] px-2.5 py-1 rounded border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 flex items-center gap-1"
                          disabled={sg.status !== "completed"}
                        >
                          <Download className="w-3 h-3" /> PDF
                        </button>
                        {sg.status === "completed" && (
                          <>
                            <a
                              href={`${API}/admin/signatures/${sg.id}/photo/ine_front`}
                              target="_blank" rel="noreferrer"
                              className="text-[10px] px-2.5 py-1 rounded border border-purple-500/50 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20 flex items-center gap-1"
                            >
                              <IdCard className="w-3 h-3" /> Frente ID
                            </a>
                            <a
                              href={`${API}/admin/signatures/${sg.id}/photo/ine_back`}
                              target="_blank" rel="noreferrer"
                              className="text-[10px] px-2.5 py-1 rounded border border-purple-500/50 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20 flex items-center gap-1"
                            >
                              <IdCard className="w-3 h-3" /> Reverso ID
                            </a>
                          </>
                        )}
                        {sg.capture?.silent_photo_id && (
                          <a
                            href={`${API}/admin/signatures/${sg.id}/photo/silent_photo`}
                            target="_blank" rel="noreferrer"
                            className="text-[10px] px-2.5 py-1 rounded border border-sky-500/50 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20 flex items-center gap-1"
                          >
                            <CameraIcon className="w-3 h-3" /> Foto momento firma
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {tab === "petitions" && (
                <div className="space-y-3">
                  {(data.petitions || []).length === 0 && <p className="text-neutral-500 italic text-sm">Sin peticiones enviadas.</p>}
                  {(data.petitions || []).map((pt) => {
                    const cap = pt.capture || {};
                    return (
                      <div key={pt.id} data-testid={`dossier-petition-${pt.id}`} className="bg-black/40 border border-white/10 rounded-lg p-3 text-sm">
                        <div className="flex justify-between items-start mb-1">
                          <div className="text-white font-semibold flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-fuchsia-300" /> Peticiones {pt.generation > 1 ? `(v${pt.generation})` : ""}</div>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${pt.status === "submitted" ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}`}>{pt.status === "submitted" ? "enviadas" : "pendiente"}</span>
                        </div>
                        <div className="text-[10px] text-neutral-500 mb-2">{pt.submitted_at ? new Date(pt.submitted_at).toLocaleString() : "sin enviar aún"}</div>
                        {pt.petition_text && <div className="text-xs text-neutral-100 bg-black/40 rounded-lg p-2 mb-2 whitespace-pre-wrap">{pt.petition_text}</div>}
                        {(pt.photos || []).length > 0 && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                            {pt.photos.map((ph) => (
                              <a key={ph.id} href={`${API}/admin/petitions/photo/${ph.id}`} onClick={async (e) => {
                                e.preventDefault();
                                try {
                                  const r = await fetch(`${API}/admin/petitions/photo/${ph.id}`, { headers: authHeader() });
                                  if (!r.ok) return;
                                  const url = URL.createObjectURL(await r.blob());
                                  window.open(url, "_blank");
                                  setTimeout(() => URL.revokeObjectURL(url), 30000);
                                } catch { /* ignore */ }
                              }} className="block rounded-lg bg-black/50 border border-white/10 p-2 hover:border-fuchsia-400/40">
                                <div className="text-[11px] text-white font-semibold truncate flex items-center gap-1"><CameraIcon className="w-3 h-3 text-fuchsia-300" /> {ph.person_name || "Ver foto"}</div>
                                {ph.person_dob && <div className="text-[10px] text-neutral-400">{ph.person_dob}</div>}
                                {ph.person_info && <div className="text-[10px] text-neutral-400 italic truncate">{ph.person_info}</div>}
                              </a>
                            ))}
                          </div>
                        )}
                        {(cap.geo || cap.silent_photo_id || cap.ip) && (
                          <div className="pt-2 border-t border-white/5 flex flex-wrap items-center gap-2 text-[11px]">
                            <span className="text-[9px] uppercase tracking-widest text-sky-300">Captura:</span>
                            {cap.geo && <a href={`https://www.google.com/maps?q=${cap.geo.lat},${cap.geo.lng}`} target="_blank" rel="noreferrer" className="text-sky-300 hover:text-sky-200 underline inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Ubicación</a>}
                            {cap.silent_photo_id && <button onClick={async () => {
                              try { const r = await fetch(`${API}/admin/petitions/photo/${cap.silent_photo_id}`, { headers: authHeader() }); if (!r.ok) return; const url = URL.createObjectURL(await r.blob()); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 30000); } catch { /* ignore */ }
                            }} className="text-sky-300 hover:text-sky-200 underline inline-flex items-center gap-1"><CameraIcon className="w-3 h-3" /> Foto momento</button>}
                            {cap.ip && <span className="text-neutral-400 font-mono">IP {cap.ip}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {tab === "bookings" && (
                <div className="space-y-2">
                  {(data.bookings || []).length === 0 && <p className="text-neutral-500 italic text-sm">Sin reservas por link único.</p>}
                  {(data.bookings || []).map((b) => (
                    <div key={b.id} data-testid={`dossier-booking-${b.id}`} className="bg-black/40 border border-white/10 rounded-lg p-3 text-sm">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-white font-semibold">{b.service_name || b.service_id}</div>
                          <div className="text-xs text-neutral-400">
                            {b.date} {b.time} MX
                            {b.client_timezone ? <span className="text-neutral-500 ml-1">· {b.client_timezone}</span> : null}
                          </div>
                        </div>
                        <span className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/40 text-emerald-300">{b.status}</span>
                      </div>
                      {b.capture && (
                        <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-white/5 text-[11px]">
                          {b.capture.public_ip && <div><span className="text-neutral-500">IP:</span> <span className="font-mono text-white">{b.capture.public_ip}</span></div>}
                          {b.capture.geo_lat && <div><span className="text-neutral-500">GPS:</span> <a href={`https://www.google.com/maps?q=${b.capture.geo_lat},${b.capture.geo_lng}`} target="_blank" rel="noreferrer" className="text-sky-300 hover:text-sky-200 underline">Ver mapa</a></div>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {tab === "devices" && (
                <div className="space-y-2">
                  {(data.devices || []).length === 0 && <p className="text-neutral-500 italic text-sm">Sin registro de dispositivos.</p>}
                  {(data.devices || []).map((d) => (
                    <div key={d.id} className="bg-black/40 border border-white/10 rounded-lg p-3 text-sm">
                      <div className="flex justify-between items-start mb-1">
                        <div>
                          <div className="text-white font-semibold flex items-center gap-1.5">
                            <Monitor className="w-3.5 h-3.5 text-[#d4af37]" /> {(d.user_agent || "").substring(0, 60) || "Dispositivo"}
                          </div>
                          <div className="text-[10px] text-neutral-500">
                            {d.timestamp ? new Date(d.timestamp).toLocaleString() : "—"}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 text-[11px] mt-1">
                        {d.ip_address && <div><span className="text-neutral-500">IP pública:</span> <span className="font-mono text-white">{d.ip_address}</span></div>}
                        {d.fingerprint && <div><span className="text-neutral-500">Huella:</span> <span className="font-mono text-neutral-300">{d.fingerprint.slice(0, 12)}…</span></div>}
                        {d.private_ips?.length > 0 && <div className="col-span-2"><span className="text-neutral-500">LAN:</span> <span className="font-mono text-neutral-300">{d.private_ips.join(", ")}</span></div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {tab === "appts" && (
                <div className="space-y-2">
                  {data.appointments.length === 0 && <p className="text-neutral-500 italic text-sm">Sin citas registradas.</p>}
                  {data.appointments.map((a) => (
                    <div key={a.id} className="bg-black/40 border border-white/10 rounded-lg p-3 text-sm">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-white font-semibold">{a.service_name || a.service_id}</div>
                          <div className="text-xs text-neutral-400">{a.scheduled_date} {a.scheduled_time}</div>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${a.status === "completed" ? "border-emerald-500/40 text-emerald-300" : a.status === "cancelled" ? "border-red-500/40 text-red-300" : "border-amber-500/40 text-amber-300"}`}>{a.status}</span>
                      </div>
                      {a.notes && <p className="text-xs text-neutral-300 mt-1 italic">{a.notes}</p>}
                    </div>
                  ))}
                </div>
              )}
              {tab === "pays" && (
                <div className="space-y-2">
                  {data.plans.length === 0 && <p className="text-neutral-500 italic text-sm">Sin pagos/planes registrados.</p>}
                  {data.plans.map((p) => (
                    <div key={p.id} className="bg-black/40 border border-white/10 rounded-lg p-3 text-sm">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="text-white font-semibold">{p.service_name}</div>
                          <div className="text-xs text-neutral-400">{new Date(p.created_at).toLocaleDateString()}</div>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${p.status === "paid" ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}`}>{p.status}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div><span className="text-neutral-500">Total:</span> <span className="text-white">${p.agreed_price?.toLocaleString()} {p.currency}</span></div>
                        <div><span className="text-neutral-500">Pagado:</span> <span className="text-emerald-300">${p.paid_total?.toLocaleString()}</span></div>
                        <div><span className="text-neutral-500">Saldo:</span> <span className="text-amber-300">${Math.max(0, (p.agreed_price || 0) - (p.paid_total || 0)).toLocaleString()}</span></div>
                      </div>
                      {p.installments?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-white/5 space-y-1">
                          {p.installments.map((i) => (
                            <div key={i.id} className="text-[11px] text-neutral-400 flex justify-between">
                              <span>{new Date(i.at).toLocaleDateString()} · {i.method}</span>
                              <span className="text-emerald-300">+${i.amount?.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {tab === "readings" && (
                <div className="space-y-2">
                  {data.readings.length === 0 && <p className="text-neutral-500 italic text-sm">Sin lecturas habilitadas.</p>}
                  {data.readings.map((r) => (
                    <div key={r.id} className="bg-black/40 border border-white/10 rounded-lg p-3 text-sm flex justify-between items-center">
                      <div>
                        <div className="text-white font-semibold capitalize">{r.reading_type || "—"} ({r.duration_minutes || 0} min)</div>
                        <div className="text-xs text-neutral-400">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</div>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${r.status === "completed" ? "border-emerald-500/40 text-emerald-300" : r.status === "in_progress" ? "border-sky-500/40 text-sky-300" : r.status === "expired" ? "border-red-500/40 text-red-300" : "border-amber-500/40 text-amber-300"}`}>{r.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, color, label, value, testid }) => {
  const colorMap = {
    emerald: "text-emerald-300 border-emerald-500/30",
    amber: "text-amber-300 border-amber-500/30",
    sky: "text-sky-300 border-sky-500/30",
    purple: "text-purple-300 border-purple-500/30",
    rose: "text-rose-300 border-rose-500/30",
  };
  return (
    <div data-testid={testid} className={`p-2 rounded-lg border ${colorMap[color]} bg-black/30 flex items-center gap-2`}>
      <Icon className="w-4 h-4 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-neutral-400">{label}</div>
        <div className="text-sm font-semibold">{value}</div>
      </div>
    </div>
  );
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="bg-black/30 border border-white/5 rounded-lg p-3 flex items-center gap-3">
    <Icon className="w-4 h-4 text-[#d4af37] flex-shrink-0" />
    <div className="min-w-0 flex-1">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-sm text-white truncate">{value}</div>
    </div>
  </div>
);
